import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const token = () => localStorage.getItem('token');

const API = (path, opts = {}) =>
  fetch(path, {
    headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });

const STATUS_COLOURS = {
  awaiting_consent: ['#fef9c3', '#854d0e'],
  pending:          ['#fef3c7', '#92400e'],
  declined:         ['#fee2e2', '#991b1b'],
  accepted:         ['#dbeafe', '#1e40af'],
  completed:        ['#d1fae5', '#065f46'],
  paid:             ['#dcfce7', '#166534'],
  consent_expired:  ['#f3f4f6', '#6b7280'],
  cancelled:        ['#f3f4f6', '#6b7280'],
};
const STATUS_LABELS = {
  awaiting_consent: 'Awaiting Consent',
  pending:          'Pending',
  declined:         'Declined',
  accepted:         'Accepted',
  completed:        'Completed',
  paid:             'Paid',
  consent_expired:  'Consent Expired',
  cancelled:        'Cancelled',
};
const PARTNER_NEXT = { pending: 'accepted', accepted: 'completed' };

export default function PartnerPortal() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  // Commission entry modal
  const [commModal, setCommModal] = useState(null);
  const [commForm, setCommForm] = useState({ commission_amount: '', notes: '' });
  const [commSaving, setCommSaving] = useState(false);
  const [commError, setCommError] = useState('');

  const [advancing, setAdvancing] = useState({});

  // Edit profile modal
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [aiUrl, setAiUrl] = useState('');
  const [aiFilling, setAiFilling] = useState(false);
  const [aiError, setAiError] = useState('');

  const openEditProfile = () => {
    if (!profile) return;
    setProfileForm({
      about:         profile.about        || '',
      contact_name:  profile.contact_name || '',
      contact_email: profile.contact_email|| '',
      website:       profile.website      || '',
      usp_bullets:   (profile.usp_bullets && profile.usp_bullets.length) ? [...profile.usp_bullets] : [''],
      services:      (profile.services    && profile.services.length)    ? profile.services.map(s => ({ name: s.name || '', price: s.price || '', description: s.description || '' })) : [{ name: '', price: '', description: '' }],
    });
    setAiUrl(profile.website || '');
    setAiError('');
    setProfileError('');
    setShowEditProfile(true);
  };

  const saveProfile = async () => {
    setProfileSaving(true); setProfileError('');
    try {
      const usp_bullets = profileForm.usp_bullets.filter(b => b.trim());
      const services    = profileForm.services.filter(s => s.name.trim());
      const r = await API('/api/partners/portal/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          about:         profileForm.about         || null,
          contact_name:  profileForm.contact_name  || null,
          contact_email: profileForm.contact_email || null,
          website:       profileForm.website        || null,
          usp_bullets,
          services,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setProfileError(d.error || 'Failed to save'); setProfileSaving(false); return; }
      setProfile(p => ({ ...p, ...d.partner }));
      setShowEditProfile(false);
    } catch { setProfileError('Network error — please try again'); }
    setProfileSaving(false);
  };

  const runAiFill = async () => {
    if (!aiUrl) { setAiError('Enter the website URL first'); return; }
    setAiFilling(true); setAiError('');
    try {
      const r = await API('/api/partners/ai-profile', { method: 'POST', body: JSON.stringify({ url: aiUrl }) });
      const d = await r.json();
      if (!r.ok) { setAiError(d.error || 'AI fill failed'); setAiFilling(false); return; }
      setProfileForm(f => ({
        ...f,
        website: aiUrl,
        about: d.about || f.about,
        usp_bullets: (d.usp_bullets && d.usp_bullets.length) ? d.usp_bullets : f.usp_bullets,
        services: (d.services && d.services.length) ? d.services.map(sv => ({ name: sv.name || '', price: sv.price || '', description: sv.description || '' })) : f.services,
        contact_name: d.contact_name || f.contact_name,
        contact_email: d.contact_email || f.contact_email,
      }));
    } catch { setAiError('Network error — check the URL and try again'); }
    setAiFilling(false);
  };

  const setPBullet = (i, val) => setProfileForm(f => { const b = [...f.usp_bullets]; b[i] = val; return { ...f, usp_bullets: b }; });
  const addPBullet = () => setProfileForm(f => ({ ...f, usp_bullets: [...f.usp_bullets, ''] }));
  const removePBullet = (i) => setProfileForm(f => ({ ...f, usp_bullets: f.usp_bullets.filter((_, j) => j !== i) }));
  const setPService = (i, field, val) => setProfileForm(f => { const sv = f.services.map((svc, j) => j === i ? { ...svc, [field]: val } : svc); return { ...f, services: sv }; });
  const addPService = () => setProfileForm(f => ({ ...f, services: [...f.services, { name: '', price: '', description: '' }] }));
  const removePService = (i) => setProfileForm(f => ({ ...f, services: f.services.filter((_, j) => j !== i) }));

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const load = useCallback(async () => {
    setLoading(true);
    const qs = statusFilter ? `?status=${statusFilter}` : '';
    const [profRes, statsRes, refRes] = await Promise.all([
      API('/api/partners/portal/me'),
      API('/api/partners/portal/stats'),
      API(`/api/partners/portal/referrals${qs}`),
    ]);
    const [prof, st, refs] = await Promise.all([profRes.json(), statsRes.json(), refRes.json()]);
    if (profRes.ok) setProfile(prof);
    if (statsRes.ok) setStats(st);
    setReferrals(Array.isArray(refs) ? refs : []);
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const advance = async (ref) => {
    const next = PARTNER_NEXT[ref.status];
    if (!next) return;
    setAdvancing(a => ({ ...a, [ref.id]: true }));
    await API(`/api/partners/portal/referrals/${ref.id}`, {
      method: 'PATCH', body: JSON.stringify({ status: next }),
    });
    await load();
    setAdvancing(a => ({ ...a, [ref.id]: false }));
  };

  const openCommModal = (ref) => {
    setCommModal(ref);
    setCommForm({ commission_amount: ref.commission_amount ? String(ref.commission_amount) : '', notes: ref.notes || '' });
    setCommError('');
  };

  const saveCommission = async () => {
    if (!commForm.commission_amount) { setCommError('Enter the commission amount'); return; }
    setCommSaving(true); setCommError('');
    const r = await API(`/api/partners/portal/referrals/${commModal.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        commission_amount: parseFloat(commForm.commission_amount),
        status: 'completed',
        notes: commForm.notes || undefined,
      }),
    });
    const d = await r.json();
    if (!r.ok) { setCommError(d.error || 'Failed'); setCommSaving(false); return; }
    setCommModal(null);
    setCommSaving(false);
    load();
  };

  const statCards = stats ? [
    { label: 'Awaiting consent', value: stats.awaiting_consent, bg: '#fef9c3', col: '#854d0e' },
    { label: 'Active referrals', value: parseInt(stats.pending) + parseInt(stats.accepted), bg: '#dbeafe', col: '#1e40af' },
    { label: 'Completed', value: parseInt(stats.completed) + parseInt(stats.paid), bg: '#d1fae5', col: '#065f46' },
    { label: 'Total commission', value: `£${parseFloat(stats.total_commission).toFixed(2)}`, bg: '#f0fdf4', col: '#065f46' },
  ] : [];

  const visibleRefs = referrals.filter(r => !statusFilter || r.status === statusFilter);
  const activeTabs = [...new Set(referrals.map(r => r.status))];

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Top nav */}
      <nav style={{ background: '#0e1b2c', padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '60px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: '#10b981', fontWeight: 800, fontSize: '18px' }}>Broker</span>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: '18px' }}>Connect</span>
          <span style={{ color: '#4b5563', fontSize: '14px', marginLeft: '12px' }}>Partner Portal</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ color: '#9ca3af', fontSize: '13px' }}>
            {profile?.name || user.name}
          </span>
          <button onClick={logout} style={{ background: 'none', border: '1px solid #374151', borderRadius: '6px', color: '#9ca3af', padding: '5px 12px', fontSize: '12px', cursor: 'pointer' }}>
            Sign out
          </button>
        </div>
      </nav>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#0e1b2c', margin: '0 0 4px 0' }}>
            {profile ? `${profile.category_icon || ''} ${profile.name}` : 'Partner Portal'}
          </h1>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>
            Manage inbound referrals from broker partners
          </p>
        </div>

        {loading ? (
          <div style={{ color: '#6b7280', padding: '60px 0', textAlign: 'center' }}>Loading…</div>
        ) : (
          <>
            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '28px' }}>
              {statCards.map(({ label, value, bg, col }) => (
                <div key={label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
                  <div style={{ fontSize: '26px', fontWeight: 800, color: col, fontFamily: 'Barlow Condensed, sans-serif' }}>{value}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Profile card */}
            {profile && (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: '#111827' }}>{profile.name}</div>
                    {(profile.about || profile.description) && (
                      <div style={{ fontSize: '13px', color: '#374151', marginTop: '4px', lineHeight: 1.5 }}>{profile.about || profile.description}</div>
                    )}

                    {/* USP bullets */}
                    {profile.usp_bullets && profile.usp_bullets.length > 0 && (
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '18px' }}>
                        {profile.usp_bullets.map((b, i) => (
                          <li key={i} style={{ fontSize: '12px', color: '#374151', marginBottom: '2px' }}>{b}</li>
                        ))}
                      </ul>
                    )}

                    {/* Service chips */}
                    {profile.services && profile.services.length > 0 && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                        {profile.services.map((sv, i) => (
                          <span key={i} style={{ fontSize: '11px', background: '#f0fdf4', color: '#065f46', padding: '2px 10px', borderRadius: '999px', border: '1px solid #bbf7d0', fontWeight: 600 }}>
                            {sv.name}{sv.price ? ` — ${sv.price}` : ''}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Contact row */}
                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '12px', color: '#9ca3af', flexWrap: 'wrap' }}>
                      {profile.contact_name && <span>👤 {profile.contact_name}</span>}
                      {profile.contact_email && <span>✉ {profile.contact_email}</span>}
                      {profile.website && <span>🌐 {profile.website.replace('https://', '')}</span>}
                      <span style={{ color: '#059669', fontWeight: 600 }}>
                        Commission: {profile.commission_type === 'percentage' ? `${profile.commission_value}%` : `£${profile.commission_value}`}
                      </span>
                    </div>
                  </div>

                  {/* Right actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', flexShrink: 0 }}>
                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', fontWeight: 600, background: '#d1fae5', color: '#065f46' }}>Active</span>
                    <button onClick={openEditProfile} style={{ fontSize: '12px', padding: '5px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      Edit profile
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Status filters */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <button onClick={() => setStatusFilter('')} style={filterBtn(!statusFilter)}>
                All ({referrals.length})
              </button>
              {activeTabs.map(s => {
                const [bg, col] = STATUS_COLOURS[s] || ['#f3f4f6', '#6b7280'];
                const active = statusFilter === s;
                return (
                  <button key={s} onClick={() => setStatusFilter(s)} style={{
                    border: 'none', borderRadius: 999, padding: '5px 14px', cursor: 'pointer',
                    fontSize: '12px', fontWeight: active ? 700 : 500,
                    background: active ? col : bg, color: active ? '#fff' : col,
                  }}>
                    {STATUS_LABELS[s] || s} ({referrals.filter(r => r.status === s).length})
                  </button>
                );
              })}
            </div>

            {/* Referrals table */}
            <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1.2fr 1fr 1.5fr', padding: '10px 20px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['Client', 'From broker', 'Received', 'Status', 'Commission', 'Actions'].map(h => (
                  <div key={h} style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</div>
                ))}
              </div>

              {visibleRefs.length === 0 ? (
                <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
                  {referrals.length === 0 ? 'No inbound referrals yet — brokers who refer clients will appear here.' : 'No referrals match this filter.'}
                </div>
              ) : (
                visibleRefs.map(ref => {
                  const [bg, col] = STATUS_COLOURS[ref.status] || ['#f3f4f6', '#6b7280'];
                  const next = PARTNER_NEXT[ref.status];
                  const canEnterCommission = ['accepted', 'completed'].includes(ref.status);
                  return (
                    <div key={ref.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1.2fr 1fr 1.5fr', padding: '14px 20px', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>

                      {/* Client */}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#0e1b2c' }}>{ref.client_name}</div>
                        {ref.client_email && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{ref.client_email}</div>}
                        {ref.client_phone && <div style={{ fontSize: '12px', color: '#6b7280' }}>{ref.client_phone}</div>}
                        {ref.notes && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', fontStyle: 'italic' }}>"{ref.notes}"</div>}
                      </div>

                      {/* Broker */}
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{ref.broker_firm}</div>
                        {ref.broker_name && <div style={{ fontSize: '12px', color: '#9ca3af' }}>{ref.broker_name}</div>}
                      </div>

                      {/* Date */}
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {new Date(ref.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>

                      {/* Status */}
                      <div>
                        <span style={{ background: bg, color: col, padding: '3px 10px', borderRadius: 999, fontSize: '11px', fontWeight: 700 }}>
                          {STATUS_LABELS[ref.status] || ref.status}
                        </span>
                      </div>

                      {/* Commission */}
                      <div>
                        {ref.commission_amount ? (
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#065f46' }}>£{parseFloat(ref.commission_amount).toFixed(2)}</div>
                            {ref.introducer_amount && (
                              <div style={{ fontSize: '11px', color: '#9ca3af' }}>Intro share: £{parseFloat(ref.introducer_amount).toFixed(2)}</div>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>—</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        {/* Advance status */}
                        {next && (
                          <button disabled={!!advancing[ref.id]} onClick={() => advance(ref)}
                            style={{ background: '#0e1b2c', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            {advancing[ref.id] ? '…' : `Mark ${next}`}
                          </button>
                        )}
                        {/* Enter / update commission */}
                        {canEnterCommission && (
                          <button onClick={() => openCommModal(ref)}
                            style={{ background: ref.commission_amount ? '#f0fdf4' : '#10b981', color: ref.commission_amount ? '#065f46' : '#fff', border: ref.commission_amount ? '1px solid #bbf7d0' : 'none', borderRadius: 6, padding: '5px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            {ref.commission_amount ? 'Update amount' : 'Enter commission'}
                          </button>
                        )}
                        {['paid', 'declined', 'cancelled', 'consent_expired'].includes(ref.status) && !canEnterCommission && (
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {STATUS_LABELS[ref.status]}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {/* Commission modal */}
      {commModal && (
        <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setCommModal(null); }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
                {commModal.commission_amount ? 'Update commission' : 'Enter commission received'}
              </h2>
              <button onClick={() => setCommModal(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af' }}>&times;</button>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '18px', fontSize: '13px', color: '#374151' }}>
              <div style={{ fontWeight: 600 }}>{commModal.client_name}</div>
              <div style={{ color: '#6b7280' }}>Referred by {commModal.broker_firm}</div>
            </div>

            {commError && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' }}>{commError}</div>}

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Commission amount (£) *</label>
              <input
                type="number" step="0.01" min="0" autoFocus
                value={commForm.commission_amount}
                onChange={e => setCommForm(f => ({ ...f, commission_amount: e.target.value }))}
                style={inputStyle} placeholder="e.g. 500.00"
              />
              {commModal.introducer_share_pct && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                  Introducer share ({commModal.introducer_share_pct}%) will be auto-calculated: £{commForm.commission_amount ? (parseFloat(commForm.commission_amount) * commModal.introducer_share_pct / 100).toFixed(2) : '—'}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={labelStyle}>Notes (optional)</label>
              <textarea
                value={commForm.notes}
                onChange={e => setCommForm(f => ({ ...f, notes: e.target.value }))}
                style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                placeholder="Any notes about this referral outcome…"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button onClick={() => setCommModal(null)} style={{ padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>
                Cancel
              </button>
              <button onClick={saveCommission} disabled={commSaving} style={{ padding: '10px', border: 'none', borderRadius: '8px', background: '#10b981', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>
                {commSaving ? 'Saving…' : 'Save & mark completed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Profile Modal ── */}
      {showEditProfile && (
        <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setShowEditProfile(false); }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '28px', maxWidth: '520px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#0e1b2c', margin: 0 }}>Edit Partner Profile</h2>
              <button onClick={() => setShowEditProfile(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>&times;</button>
            </div>

            {/* AI Auto-fill strip */}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#065f46', marginBottom: '8px' }}>✨ Auto-fill profile from website</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input value={aiUrl} onChange={e => setAiUrl(e.target.value)}
                  style={{ ...inputStyle, flex: 1, background: '#fff' }} placeholder="https://yourwebsite.com"
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), runAiFill())} />
                <button type="button" onClick={runAiFill} disabled={aiFilling}
                  style={{ padding: '8px 14px', border: 'none', borderRadius: '6px', background: '#16a34a', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', opacity: aiFilling ? 0.7 : 1 }}>
                  {aiFilling ? 'Fetching…' : 'Auto-fill'}
                </button>
              </div>
              {aiError && <div style={{ fontSize: '12px', color: '#991b1b', marginTop: '6px' }}>{aiError}</div>}
              {!aiError && !aiFilling && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>Fetches your website and uses AI to extract your profile. Review and edit before saving.</div>}
            </div>

            {profileError && (
              <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' }}>{profileError}</div>
            )}

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>About (shown on partner card)</label>
              <textarea value={profileForm.about} onChange={e => setProfileForm(f => ({ ...f, about: e.target.value }))}
                rows={3} placeholder="A short description of your business and services…"
                style={{ ...inputStyle, resize: 'vertical' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Contact name</label>
                <input value={profileForm.contact_name} onChange={e => setProfileForm(f => ({ ...f, contact_name: e.target.value }))}
                  placeholder="Full name" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Contact email</label>
                <input type="email" value={profileForm.contact_email} onChange={e => setProfileForm(f => ({ ...f, contact_email: e.target.value }))}
                  placeholder="your@email.com" style={inputStyle} />
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Website</label>
              <input value={profileForm.website} onChange={e => setProfileForm(f => ({ ...f, website: e.target.value }))}
                placeholder="https://yourwebsite.com" style={inputStyle} />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>USPs / Key selling points</label>
              {(profileForm.usp_bullets || []).map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <input value={b} onChange={e => setPBullet(i, e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder={`USP ${i + 1}`} />
                  {profileForm.usp_bullets.length > 1 && (
                    <button type="button" onClick={() => removePBullet(i)} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff', color: '#991b1b', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addPBullet} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', color: '#374151' }}>+ Add bullet</button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Services offered</label>
              {(profileForm.services || []).map((sv, i) => (
                <div key={i} style={{ marginBottom: '8px', padding: '10px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                    <input value={sv.name} onChange={e => setPService(i, 'name', e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Service name" />
                    <input value={sv.price || ''} onChange={e => setPService(i, 'price', e.target.value)} style={{ ...inputStyle, width: '120px' }} placeholder="Price / range" />
                    {profileForm.services.length > 1 && (
                      <button type="button" onClick={() => removePService(i)} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff', color: '#991b1b', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                    )}
                  </div>
                  <input value={sv.description || ''} onChange={e => setPService(i, 'description', e.target.value)} style={inputStyle} placeholder="Brief description" />
                </div>
              ))}
              <button type="button" onClick={addPService} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', color: '#374151' }}>+ Add service</button>
            </div>

            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px', fontSize: '12px', color: '#6b7280' }}>
              Commission rates are managed by BrokerConnect and cannot be changed here.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button onClick={() => setShowEditProfile(false)} style={{ padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>
                Cancel
              </button>
              <button onClick={saveProfile} disabled={profileSaving} style={{ padding: '10px', border: 'none', borderRadius: '8px', background: '#0e1b2c', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 700, opacity: profileSaving ? 0.7 : 1 }}>
                {profileSaving ? 'Saving…' : 'Save profile'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' };
const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', color: '#111827', boxSizing: 'border-box' };
const filterBtn = (active) => ({ border: 'none', borderRadius: 999, padding: '5px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: active ? 700 : 500, background: active ? '#0e1b2c' : '#e5e7eb', color: active ? '#fff' : '#374151' });
